import React from 'react';
import { useQuery } from '@apollo/client';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { Refund } from '@atlassian/cssxp-types-billing';
import {
  FetchAuditNotesForEntityQuery,
  FetchAuditNotesForEntityQueryVariables,
  SortDirection,
  AuditNote,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { NoData } from '@css-xp/ppc-common';
import { SummaryPanelRow, fetchAuditNotesForEntity, FormattedDate } from '@css-xp/base';

const defaultSortOptions = {
  sortFields: ['created', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
};

const defaultAuditNotesSearchOptions: FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  sort: defaultSortOptions,
};

interface RefundPaidDateProps {
  refund: Refund;
  label?: string;
  hideTime?: boolean;
  dateFormat?: string;
}

export const RefundPaidDate = ({ refund, label, hideTime, dateFormat }: RefundPaidDateProps) => {
  const { data, loading: auditNotesLoading } = useQuery<
    FetchAuditNotesForEntityQuery,
    FetchAuditNotesForEntityQueryVariables
  >(fetchAuditNotesForEntity, {
    variables: {
      searchParams: {
        and: [{ refundKey: refund?.creditNoteId }],
      },
      searchOptions: defaultAuditNotesSearchOptions,
    },
    displayName: 'fetchAuditNotesByEntityId',
    notifyOnNetworkStatusChange: true,
    skip: !refund?.creditNoteId,
  });

  const renderRefundPaidDate = () => {
    const queryResults = data?.auditNotes;
    const auditTrailNodes = queryResults?.nodes as AuditNote[];
    const paidCreditNote = auditTrailNodes?.find((audit) => audit.creditNoteStatus === 'PAID');

    return paidCreditNote?.created ? (
      <FormattedDate date={paidCreditNote?.created} showTime={!hideTime} format={dateFormat || NEW_DATE_FORMAT} />
    ) : (
      <NoData testId="no-paid-date" />
    );
  };

  return (
    <SummaryPanelRow
      label={label ?? 'Paid date'}
      value={
        <p>
          {auditNotesLoading ? (
            <SkeletonLoaderWrapper>
              <SkeletonLoader testId="paid-date-loading" width="80%" />
            </SkeletonLoaderWrapper>
          ) : (
            renderRefundPaidDate()
          )}
        </p>
      }
    />
  );
};
